import { useEffect, useState } from 'react';

/**
 * Whether the window is narrower than a breakpoint.
 *
 * The public site renders with inline styles — it runs on the brand's own
 * domain, outside any stylesheet of ours — and an inline style cannot carry a
 * media query. So the few places where the layout genuinely changes shape,
 * rather than merely reflowing, ask here instead.
 *
 * Reflow is still the first answer: wrapping, `auto-fit` grids and `min()`
 * widths handle almost everything without JavaScript. This is for the cases
 * where the same markup cannot serve both, like a menu that is a row on a
 * desktop and a panel on a phone.
 */
export default function useIsNarrow(maxWidth = 860) {
  const [narrow, setNarrow] = useState(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(`(max-width: ${maxWidth}px)`).matches
      : false
  ));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = (e) => setNarrow(e.matches);
    setNarrow(mq.matches);
    // addListener is the old name, still the only one in older Safari.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [maxWidth]);

  return narrow;
}
